import { z } from 'zod';
import { commissioningCommonEconomySchema, commissioningMaturationModeSchema } from './commissioning-competition-context';
import { COMMISSIONING_CLUSTER_ENTITY_TYPES } from '../types/commissioning';

const uuid = z.string().uuid();
const axisKey = z.string().trim().toLowerCase().regex(
  /^(overall|(?:instance:)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/,
);
export const commissioningCompetitionMatrixQuerySchema = z.object({
  q: z.string().trim().max(200).default(''),
  page: z.coerce.number().int().positive().max(100_000).default(1),
  page_size: z.coerce.number().int().positive().max(200).default(50),
  axes: z.union([z.string(), z.array(axisKey)]).default('overall')
    .transform((value) => typeof value === 'string' ? value.split(',') : value)
    .pipe(z.array(axisKey).min(1).max(50))
    .refine((values) => new Set(values).size === values.length, 'Colonna duplicata'),
}).strict();
export type CommissioningCompetitionMatrixQuery = z.infer<typeof commissioningCompetitionMatrixQuerySchema>;

const axisDescriptorObject = z.object({
  key: axisKey,
  kind: z.enum(['overall', 'driver', 'instance']),
  business_driver_id: uuid.nullable(),
  configurator_instance_id: uuid.nullable(),
  parent_key: axisKey.nullable(),
  label: z.string(),
  eligible: z.boolean(),
}).strict();
function validateAxis(value: z.infer<typeof axisDescriptorObject>, ctx: z.RefinementCtx): void {
  const valid = value.kind === 'overall'
    ? value.key === 'overall' && value.business_driver_id === null && value.configurator_instance_id === null && value.parent_key === null
    : value.kind === 'driver'
      ? value.business_driver_id !== null && value.configurator_instance_id === null && value.key === value.business_driver_id && value.parent_key === 'overall'
      : value.configurator_instance_id !== null && value.business_driver_id === null && value.key === `instance:${value.configurator_instance_id}`
        && value.parent_key !== null && !value.parent_key.startsWith('instance:');
  if (!valid) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Identità dell’asse incoerente' });
}
export const commissioningMatrixAxisDescriptorSchema = axisDescriptorObject.superRefine(validateAxis);
export const commissioningCompetitionMatrixClusterSchema = z.object({
  id: uuid, name: z.string(), source_cluster_id: uuid.nullable(), source_cluster_name: z.string().nullable(),
  member_count: z.number().int().nonnegative(), active_assignment_count: z.number().int().nonnegative(),
}).strict();
export const commissioningCompetitionMatrixAxisSchema = axisDescriptorObject.extend({
  clusters: z.array(commissioningCompetitionMatrixClusterSchema),
  /** Explicit placements, not a promise of economic readiness or inherited coverage. */
  explicit_count: z.number().int().nonnegative(),
}).strict().superRefine(validateAxis);
export const commissioningCompetitionMatrixCellSchema = z.object({
  key: axisKey, business_driver_id: uuid.nullable(), configurator_instance_id: uuid.nullable(),
  cluster_id: uuid.nullable(), member_id: uuid.nullable(),
}).strict().superRefine((value, ctx) => {
  const expected = value.configurator_instance_id !== null ? `instance:${value.configurator_instance_id}` : value.business_driver_id ?? 'overall';
  if (value.key !== expected || (value.business_driver_id !== null && value.configurator_instance_id !== null)
    || (value.cluster_id === null) !== (value.member_id === null)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Cella di classificazione incoerente' });
  }
});
export const commissioningCompetitionMatrixRowSchema = z.object({
  row_id: uuid,
  kind: commissioningMaturationModeSchema,
  entity_type: z.enum(COMMISSIONING_CLUSTER_ENTITY_TYPES),
  entity_id: uuid.nullable(),
  economic_subject_id: uuid.nullable(),
  participant_id: uuid.nullable(),
  company_id: uuid.nullable(), store_id: uuid.nullable(), user_id: uuid.nullable(),
  label: z.string(), code: z.string().nullable(),
  component_count: z.number().int().nonnegative(),
  cells: z.array(commissioningCompetitionMatrixCellSchema),
}).strict().superRefine((value, ctx) => {
  const parties = [value.company_id, value.store_id, value.user_id].filter((id) => id !== null);
  const entity = value.entity_type === 'rs' ? value.company_id : value.entity_type === 'pdv' ? value.store_id : value.user_id;
  const valid = value.kind === 'collective'
    ? parties.length === 0 && value.entity_id === null && value.participant_id === null && value.economic_subject_id === value.row_id
    : parties.length === 1 && entity === value.entity_id && value.component_count === 1
      && value.row_id === value.economic_subject_id && value.participant_id !== null;
  if (!valid) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Identità della riga economica incoerente' });
});
export const commissioningCompetitionClusterMatrixSchema = z.object({
  competition_id: uuid,
  entity_type: z.enum(COMMISSIONING_CLUSTER_ENTITY_TYPES),
  context: commissioningCommonEconomySchema,
  available_axes: z.array(commissioningMatrixAxisDescriptorSchema),
  axes: z.array(commissioningCompetitionMatrixAxisSchema),
  rows: z.array(commissioningCompetitionMatrixRowSchema),
  total: z.number().int().nonnegative(),
  full_roster_count: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  page_size: z.number().int().positive(),
  visibility_limited: z.boolean(),
}).strict().superRefine((value, ctx) => {
  const available = new Map(value.available_axes.map((axis) => [axis.key, axis]));
  const selected = new Set(value.axes.map((axis) => axis.key));
  if (available.size !== value.available_axes.length || selected.size !== value.axes.length
    || new Set(value.rows.map((row) => row.row_id)).size !== value.rows.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Righe o assi duplicati' });
  }
  for (const [index, axis] of value.axes.entries()) {
    const descriptor = available.get(axis.key);
    if (!descriptor || descriptor.kind !== axis.kind || descriptor.business_driver_id !== axis.business_driver_id
      || descriptor.configurator_instance_id !== axis.configurator_instance_id || descriptor.parent_key !== axis.parent_key) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['axes', index], message: 'Asse non appartenente al catalogo della gara' });
    }
  }
  for (const [index, row] of value.rows.entries()) {
    if (row.cells.length !== selected.size || new Set(row.cells.map((cell) => cell.key)).size !== selected.size
      || row.cells.some((cell) => !selected.has(cell.key))
      || row.kind !== value.context.maturation_mode
      || row.entity_type !== value.entity_type) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['rows', index], message: 'Riga estranea al contesto o con celle incomplete' });
    }
  }
  if (value.context.participant_type !== value.entity_type) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['entity_type'], message: 'Tipo non coerente con il roster' });
  }
});
